import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { fetchAdminBucketStocks, createAdminBucketStock, updateAdminBucketStock, deleteAdminBucketStock } from '../../api/admin';
import { AdminDataTable, AdminPagination, AdminConfirmModal, Column } from '../../components/admin';
import { FiArrowLeft } from 'react-icons/fi';

const RATING_OPTIONS: { value: string; label: string }[] = [
  { value: 'strong_buy', label: 'Strong Buy' },
  { value: 'buy', label: 'Buy' },
  { value: 'hold', label: 'Hold' },
  { value: 'sell', label: 'Sell' },
  { value: 'strong_sell', label: 'Strong Sell' },
];

const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  functional: { bg: '#3B82F620', text: '#3B82F6' },
  sectoral: { bg: '#8B5CF620', text: '#8B5CF6' },
  valuation: { bg: '#F59E0B20', text: '#F59E0B' },
  historical: { bg: '#10B98120', text: '#10B981' },
};

const AdminBucketDetail: React.FC = () => {
  const colors = useAppSelector((state) => state.theme.theme);
  const { bucketName } = useParams<{ bucketName: string }>();
  const navigate = useNavigate();
  const decodedName = decodeURIComponent(bucketName || '');

  const [stocks, setStocks] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bucketCategory, setBucketCategory] = useState('');
  const pageSize = 20;

  // Add stock form
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ stock_name: '', stock_symbol: '', rating: '', analysis: '' });
  const [adding, setAdding] = useState(false);

  // Edit stock
  const [editStock, setEditStock] = useState<any>(null);
  const [editForm, setEditForm] = useState({ stock_name: '', stock_symbol: '', rating: '', analysis: '' });
  const [saving, setSaving] = useState(false);

  // Delete
  const [deletingStock, setDeletingStock] = useState<any>(null);

  const load = useCallback(() => {
    if (!decodedName) return;
    setLoading(true);
    fetchAdminBucketStocks(decodedName, { page, page_size: pageSize })
      .then((res) => {
        setStocks(res.data.results);
        setTotal(res.data.total);
        setBucketCategory(res.data.bucket_category || '');
        setError('');
      })
      .catch((err) => {
        const status = err?.response?.status;
        setError(status === 500
          ? 'Server error loading bucket stocks. The backend may not have this endpoint deployed yet.'
          : `Failed to load bucket stocks${status ? ` (${status})` : ''}.`
        );
      })
      .finally(() => setLoading(false));
  }, [decodedName, page]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!addForm.stock_name) return;
    setAdding(true);
    try {
      await createAdminBucketStock({
        bucket_name: decodedName,
        bucket_category: bucketCategory,
        stock_name: addForm.stock_name,
        stock_symbol: addForm.stock_symbol,
        rating: addForm.rating || null,
        analysis: addForm.analysis || null,
      });
      setShowAdd(false);
      setAddForm({ stock_name: '', stock_symbol: '', rating: '', analysis: '' });
      load();
    } catch {
      alert('Failed to add stock');
    }
    setAdding(false);
  };

  const handleEdit = async () => {
    if (!editStock) return;
    setSaving(true);
    try {
      await updateAdminBucketStock(editStock.id, {
        stock_name: editForm.stock_name,
        stock_symbol: editForm.stock_symbol,
        rating: editForm.rating || null,
        analysis: editForm.analysis || null,
      });
      setEditStock(null);
      load();
    } catch {
      alert('Failed to save changes');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deletingStock) return;
    try {
      await deleteAdminBucketStock(deletingStock.id);
      setDeletingStock(null);
      load();
    } catch {
      alert('Failed to delete stock');
    }
  };

  const catColor = CATEGORY_COLORS[bucketCategory] || { bg: '#6B728020', text: '#6B7280' };

  const columns: Column[] = [
    { key: 'stock_name', header: 'Stock Name' },
    { key: 'stock_symbol', header: 'Symbol', width: '100px' },
    {
      key: 'rating', header: 'Rating', width: '80px',
      render: (row) => {
        if (!row.rating) return '-';
        const r = row.rating as string;
        const color = (r === 'strong_buy' || r === 'buy') ? '#10B981' : r === 'hold' ? '#F59E0B' : '#EF4444';
        const label = RATING_OPTIONS.find(o => o.value === r)?.label || r;
        return (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: `${color}20`, color }}>
            {label}
          </span>
        );
      },
    },
    {
      key: 'analysis', header: 'Analysis',
      render: (row) => (
        <span className="text-xs" style={{ color: colors.grayScale500 }}>
          {row.analysis ? (row.analysis.length > 80 ? row.analysis.slice(0, 80) + '...' : row.analysis) : '-'}
        </span>
      ),
    },
    {
      key: 'updated_at', header: 'Updated', width: '110px',
      render: (row) => row.updated_at ? new Date(row.updated_at).toLocaleDateString() : '-',
    },
    {
      key: 'actions', header: '', width: '140px',
      render: (row) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setEditStock(row);
              setEditForm({
                stock_name: row.stock_name || '',
                stock_symbol: row.stock_symbol || '',
                rating: row.rating || '',
                analysis: row.analysis || '',
              });
            }}
            className="px-2 py-1 rounded-lg text-xs font-medium"
            style={{ backgroundColor: `${colors.primary}15`, color: colors.primary }}
          >
            Edit
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setDeletingStock(row); }}
            className="px-2 py-1 rounded-lg text-xs font-medium"
            style={{ backgroundColor: '#EF444415', color: '#EF4444' }}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const inputStyle = {
    backgroundColor: colors.inputBackground,
    border: `1px solid ${colors.borderColor}`,
    color: colors.textColor,
  };

  const stockFormFields = (form: typeof addForm, setForm: (f: typeof addForm) => void) => (
    <div className="space-y-3">
      <input
        placeholder="Stock Name"
        value={form.stock_name}
        onChange={(e) => setForm({ ...form, stock_name: e.target.value })}
        className="w-full px-3 py-2 rounded-xl text-sm outline-none"
        style={inputStyle}
      />
      <input
        placeholder="Stock Symbol"
        value={form.stock_symbol}
        onChange={(e) => setForm({ ...form, stock_symbol: e.target.value })}
        className="w-full px-3 py-2 rounded-xl text-sm outline-none"
        style={inputStyle}
      />
      <select
        value={form.rating}
        onChange={(e) => setForm({ ...form, rating: e.target.value })}
        className="w-full px-3 py-2 rounded-xl text-sm outline-none"
        style={inputStyle}
      >
        <option value="">No Rating</option>
        {RATING_OPTIONS.map(r => (
          <option key={r.value} value={r.value}>{r.label}</option>
        ))}
      </select>
      <textarea
        placeholder="Analysis (optional)"
        value={form.analysis}
        onChange={(e) => setForm({ ...form, analysis: e.target.value })}
        rows={3}
        className="w-full px-3 py-2 rounded-xl text-sm outline-none resize-none"
        style={inputStyle}
      />
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/admin/buckets')}
          className="p-2 rounded-xl"
          style={{ backgroundColor: colors.inputBackground }}
        >
          {React.createElement(FiArrowLeft as any, { size: 18, color: colors.textColor })}
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold" style={{ color: colors.textColor }}>{decodedName}</h1>
          <div className="flex items-center gap-2 mt-1">
            <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: catColor.bg, color: catColor.text }}>
              {bucketCategory || 'unknown'}
            </span>
            <span className="text-xs" style={{ color: colors.grayScale500 }}>{total} stocks</span>
          </div>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="px-4 py-2 rounded-xl text-sm font-medium text-white"
          style={{ backgroundColor: colors.primary }}
        >
          Add Stock
        </button>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-xl text-sm font-medium" style={{ backgroundColor: '#EF444415', color: '#EF4444', border: '1px solid #EF444430' }}>
          {error}
        </div>
      )}

      <AdminDataTable
        columns={columns}
        data={stocks}
        loading={loading}
        emptyMessage="No stocks in this bucket"
      />
      <AdminPagination page={page} totalPages={Math.ceil(total / pageSize)} onPageChange={setPage} />

      {/* Add Stock Modal */}
      {showAdd && (
        <div className="fixed inset-0 flex items-center justify-center z-[100] p-4" style={{ backgroundColor: colors.modalBackground }}>
          <div className="rounded-2xl p-6 w-full max-w-md" style={{ backgroundColor: colors.cardBackground || colors.backgroundColor, boxShadow: colors.shadowLarge }}>
            <h3 className="text-lg font-bold mb-4" style={{ color: colors.textColor }}>Add Stock to {decodedName}</h3>
            {stockFormFields(addForm, setAddForm)}
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setShowAdd(false)}
                className="flex-1 py-2.5 rounded-xl border text-sm font-medium"
                style={{ borderColor: colors.borderColor, color: colors.textColor }}
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                disabled={adding || !addForm.stock_name}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: colors.primary }}
              >
                {adding ? 'Adding...' : 'Add Stock'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Stock Modal */}
      {editStock && (
        <div className="fixed inset-0 flex items-center justify-center z-[100] p-4" style={{ backgroundColor: colors.modalBackground }}>
          <div className="rounded-2xl p-6 w-full max-w-md" style={{ backgroundColor: colors.cardBackground || colors.backgroundColor, boxShadow: colors.shadowLarge }}>
            <h3 className="text-lg font-bold mb-4" style={{ color: colors.textColor }}>Edit Stock</h3>
            {stockFormFields(editForm, setEditForm)}
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setEditStock(null)}
                className="flex-1 py-2.5 rounded-xl border text-sm font-medium"
                style={{ borderColor: colors.borderColor, color: colors.textColor }}
              >
                Cancel
              </button>
              <button
                onClick={handleEdit}
                disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: colors.primary }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deletingStock && (
        <AdminConfirmModal
          title="Remove Stock"
          message={`Remove "${deletingStock.stock_name}" from this bucket?`}
          confirmLabel="Remove"
          destructive
          onConfirm={handleDelete}
          onCancel={() => setDeletingStock(null)}
        />
      )}
    </div>
  );
};

export default AdminBucketDetail;
